<template>
  <div class="register-container">
    <!-- 移动端隐藏顶部菜单，使用更简洁的导航 -->
    <div class="mobile-header" v-if="isMobile">
      <el-icon @click="goBack" class="back-icon">
        <ArrowLeft />
      </el-icon>
      <h3 class="mobile-title">用户注册</h3>
    </div>
    <RootMenu v-else></RootMenu>
    
    <div class="register-card">
      <h2 class="register-title" v-if="!isMobile">用户注册</h2>
      
      <el-form
        ref="registerFormRef"
        :model="registerForm"
        :rules="registerRules"
        :label-position="isMobile ? 'top' : 'right'"
        :label-width="isMobile ? '0' : '100px'"
        class="register-form"
        :size="isMobile ? 'default' : 'large'"
      >
        <el-form-item label="用户名" prop="username">
          <el-input
            v-model="registerForm.username"
            :placeholder="isMobile ? '用户名（3-20位字符）' : '请输入用户名（3-20位字符）'"
            clearable
            @focus="scrollToElement('username')"
            @blur="checkUsernameAsync"
          />
          <div v-if="usernameCheck.checked" class="field-check-tip" :class="{ 'success': !usernameCheck.exists, 'error': usernameCheck.exists }">
            {{ usernameCheck.exists ? '✗ 用户名已存在' : '✓ 用户名可用' }}
          </div>
        </el-form-item>

        <!-- 密码字段（前端加密） -->
        <el-form-item label="密码" prop="password">
          <el-input
            v-model="registerForm.password"
            type="password"
            :placeholder="isMobile ? '密码（6-20位字符）' : '请输入密码（6-20位字符）'"
            clearable
            :show-password="true"
          />
          <div class="password-hint" :class="{ 'mobile-hint': isMobile }">
            <span>密码将进行加密传输，确保安全</span>
            <span v-if="isMobile">（大小写字母+数字）</span>
          </div>
        </el-form-item>
        
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input
            v-model="registerForm.confirmPassword"
            type="password"
            placeholder="请再次输入密码"
            clearable
            :show-password="true"
          />
        </el-form-item>

        <el-form-item label="单位名称" prop="company">
          <el-input
            v-model="registerForm.company"
            placeholder="请输入单位名称"
            clearable
          />
        </el-form-item>

        <el-form-item label="邮箱" prop="email">
          <el-input
            v-model="registerForm.email"
            type="email"
            placeholder="请输入邮箱"
            clearable
            inputmode="email"
            @blur="checkEmailAsync"
          />
          <div v-if="emailCheck.checked" class="field-check-tip" :class="{ 'success': !emailCheck.exists, 'error': emailCheck.exists }">
            {{ emailCheck.exists ? '✗ 邮箱已被注册' : '✓ 邮箱可用' }}
          </div>
        </el-form-item>

        <el-form-item label="手机号码" prop="phone">
          <div class="phone-code-row" :class="{ 'mobile-column': isMobile }">
            <el-input
              v-model="registerForm.phone"
              type="tel"
              placeholder="请输入手机号码"
              clearable
              class="phone-input"
              inputmode="numeric"
              pattern="[0-9]*"
              @blur="checkPhoneAsync"
              @focus="scrollToElement('phone')"
            />
            <el-button
              @click="sendSmsCode"
              :disabled="smsButtonDisabled"
              class="sms-btn"
              :class="{ 'mobile-sms-btn': isMobile }"
            >
              {{ smsButtonText }}
            </el-button>
          </div>
          <div v-if="phoneCheck.checked" class="field-check-tip" :class="{ 'success': !phoneCheck.exists, 'error': phoneCheck.exists }">
            {{ phoneCheck.exists ? '✗ 手机号已注册' : '✓ 手机号可用' }}
          </div>
          <div v-if="phoneCheck.exists" class="phone-registered-tip">
            <span>该手机号已注册</span>
            <el-button type="warning" size="small" @click="goToLogin" class="reset-pwd-btn">去登录</el-button>
          </div>
          <div v-if="smsCountdown > 0" class="sms-tips">
            验证码已发送，{{ smsCountdown }}秒后可重试
          </div>
        </el-form-item>
        
        <!-- 性别选择（决定默认头像） -->
        <el-form-item label="性别" prop="gender">
          <el-radio-group v-model="registerForm.gender" @change="onGenderChange">
            <el-radio value="male">男</el-radio>
            <el-radio value="female">女</el-radio>
          </el-radio-group>
        </el-form-item>

        <!-- 头像选择：默认按性别，可上传自定义头像并预览 -->
        <el-form-item label="头像">
          <div class="avatar-picker">
            <img :src="avatarPreview" class="avatar-preview" alt="头像预览" />
            <div class="avatar-picker-actions">
              <el-button size="small" @click="triggerAvatarInput">上传头像</el-button>
              <el-button v-if="avatarFile" size="small" text type="danger" @click="clearAvatar">使用默认头像</el-button>
              <div class="avatar-tip">未上传时按性别使用默认头像，注册后可随时更换</div>
            </div>
            <input ref="avatarInputRef" type="file" accept="image/jpeg,image/png,image/gif,image/webp" style="display:none" @change="onAvatarChange" />
          </div>
        </el-form-item>

        <!-- 短信验证码输入 -->
        <el-form-item label="短信验证码" prop="smsCode">
          <el-input
            v-model="registerForm.smsCode"
            type="text"
            inputmode="numeric"
            placeholder="请输入6位短信验证码"
            clearable
            maxlength="6"
            class="sms-code-input"
          />
        </el-form-item>

        <el-form-item label="验证码" prop="captcha">
          <div class="captcha-row" :class="{ 'mobile-column': isMobile }">
            <el-input
              v-model="registerForm.captcha"
              placeholder="请输入验证码"
              clearable
              :style="{ width: isMobile ? '100%' : '200px' }"
            />
            <div class="captcha-wrapper">
              <img
                :src="captchaImage"
                alt="验证码"
                class="captcha-img"
                @click="refreshCaptcha"
              />
              <el-icon class="refresh-icon" @click="refreshCaptcha">
                <Refresh />
              </el-icon>
            </div>
          </div>
        </el-form-item>
      </el-form>

      <div class="register-actions" :class="{ 'mobile-actions': isMobile }">
        <el-button @click="goBack" class="back-btn" v-if="!isMobile">返回</el-button>
        <el-button
          type="primary"
          @click="handleRegister"
          :loading="loading"
          class="register-btn"
          :class="{ 'mobile-register-btn': isMobile }"
        >
          注册
        </el-button>
      </div>

      <div class="login-link" :class="{ 'mobile-login-link': isMobile }">
        已有账号？
        <el-button @click="goToLogin" type="text" class="link-btn">
          立即登录
        </el-button>
      </div>
    </div>

    <!-- 移动端底部操作栏 -->
    <div class="mobile-bottom-bar" v-if="isMobile">
      <el-button @click="goToLogin" type="text" class="mobile-login-btn">
        已有账号？去登录
      </el-button>
    </div>

    <MyFooter v-if="!isMobile"></MyFooter>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onBeforeUnmount, computed } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { ArrowLeft, Refresh } from '@element-plus/icons-vue'
import RootMenu from './RootMenu.vue'
import MyFooter from './MyFooter.vue'
import axios from 'axios'
import JSEncrypt from 'jsencrypt'
import config from '../config.js'

// 响应式判断
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768
}

// 短信按钮状态
const smsButtonDisabled = ref(false)
const smsButtonText = ref('获取验证码')
const smsCountdown = ref(0)
let countdownTimer = null

const router = useRouter()
const registerFormRef = ref()
const loading = ref(false)

// 注册表单
const registerForm = reactive({
  username: '',
  password: '',
  confirmPassword: '',
  company: '',
  email: '',
  phone: '',
  smsCode: '',
  captcha: '',
  gender: 'female' // 默认性别女，对应默认头像 defaultWoman.jpg
})

// 验证码图片
const captchaImage = ref('')
const captchaKey = ref('')
// 验证码加载锁：防止同一毫秒并发请求导致key冲突（表现为"刚刷新就过期"）
let captchaLoading = false

// 头像：按性别默认头像，可上传自定义头像预览（注册成功后上传到服务器）
const avatarInputRef = ref(null)
const avatarFile = ref(null)
const avatarPreview = ref('/static/image/profilePhoto/defaultWoman.jpg')

const defaultAvatarByGender = (gender) => {
  return '/static/image/profilePhoto/' + (gender === 'female' ? 'defaultWoman.jpg' : 'defautMan.jpg')
}

const onGenderChange = (gender) => {
  if (!avatarFile.value) {
    avatarPreview.value = defaultAvatarByGender(gender)
  }
}

const triggerAvatarInput = () => {
  if (avatarInputRef.value) avatarInputRef.value.click()
}

const onAvatarChange = (e) => {
  const file = e.target.files && e.target.files[0]
  e.target.value = ''
  if (!file) return
  if (file.size > 5 * 1024 * 1024) {
    ElMessage.warning('头像图片大小不能超过5M')
    return
  }
  avatarFile.value = file
  avatarPreview.value = URL.createObjectURL(file)
}

const clearAvatar = () => {
  avatarFile.value = null
  avatarPreview.value = defaultAvatarByGender(registerForm.gender)
}

// 移动端适配的表单验证规则
const registerRules = computed(() => ({
  username: [
    { required: true, message: isMobile.value ? '请输入用户名' : '请输入用户名', trigger: 'blur' },
    { min: 3, max: 20, message: '用户名长度在3到20个字符', trigger: 'blur' },
    { 
      pattern: /^[a-zA-Z0-9_]+$/,
      message: '用户名只能包含字母、数字和下划线',
      trigger: 'blur'
    }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' },
    { min: 6, max: 20, message: '密码长度在6到20个字符', trigger: 'blur' },
    {
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/,
      message: '密码必须包含大小写字母和数字',
      trigger: 'blur'
    }
  ],
  confirmPassword: [
    { required: true, message: '请确认密码', trigger: 'blur' },
    {
      validator: (rule, value, callback) => {
        if (value !== registerForm.password) {
          callback(new Error('两次输入的密码不一致'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  company: [
    { required: true, message: '请输入单位名称', trigger: 'blur' },
    { min: 2, max: 100, message: '单位名称长度在2到100个字符', trigger: 'blur' }
  ],
  email: [
    { required: true, message: '请输入邮箱', trigger: 'blur' },
    { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
  ],
  phone: [
    { required: true, message: '请输入手机号码', trigger: 'blur' },
    {
      pattern: /^1[3-9]\d{9}$/,
      message: '请输入正确的手机号码',
      trigger: 'blur'
    }
  ],
  smsCode: [
    { required: true, message: '请输入短信验证码', trigger: 'blur' },
    { pattern: /^\d{6}$/, message: '验证码必须是6位数字', trigger: 'blur' }
  ],
  captcha: [
    { required: true, message: '请输入验证码', trigger: 'blur' }
  ]
}))

// RSA加密器
const encryptor = new JSEncrypt()
// 建议从后端接口动态获取公钥
const publicKey = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAYourPublicKeyHere...
-----END PUBLIC KEY-----`
encryptor.setPublicKey(publicKey)

// 移动端输入框聚焦时自动滚动到可视区域
const scrollToElement = (fieldName) => {
  if (!isMobile.value) return
  
  const element = document.querySelector(`[prop="${fieldName}"]`)
  if (element) {
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 300)
  }
}

// 发送短信验证码
const sendSmsCode = async () => {
  if (!/^1[3-9]\d{9}$/.test(registerForm.phone)) {
    ElMessage.warning('请输入正确的手机号码')
    return
  }
  
  try {
    // 添加防抖，防止重复点击
    if (smsButtonDisabled.value) return

    smsButtonDisabled.value = true
    loading.value = true

    const { data } = await axios.get(`${config.API_BASE_URL}/api/sms/sendCode`, {
      params: { phone: registerForm.phone },
      timeout: 10000 // 10秒超时
    })
    
    if (data.code === 200) {
      ElMessage.success('验证码发送成功')
      startCountdown()
    } else {
      ElMessage.error(data.message || '发送失败')
      smsButtonDisabled.value = false
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '发送验证码失败，请重试')
    smsButtonDisabled.value = false
  } finally {
    loading.value = false
  }
}

// 开始倒计时
const startCountdown = () => {
  smsCountdown.value = 60
  smsButtonText.value = `${smsCountdown.value}秒后重试`
  
  countdownTimer = setInterval(() => {
    smsCountdown.value--
    smsButtonText.value = `${smsCountdown.value}秒后重试`
    
    if (smsCountdown.value <= 0) {
      clearInterval(countdownTimer)
      smsButtonDisabled.value = false
      smsButtonText.value = '获取验证码'
      smsCountdown.value = 0
    }
  }, 1000)
}

// 密码加密
const encryptPassword = (password) => {
  try {
    const encrypted = encryptor.encrypt(password)
    return encrypted || password
  } catch (error) {
    console.error('密码加密失败:', error)
    return password
  }
}

// 获取验证码
const refreshCaptcha = async () => {
  if (captchaLoading) return
  captchaLoading = true
  try {
    // 每次刷新都用全新唯一key，避免复用key并发覆盖导致"刚刷新就过期"
    const freshKey = Date.now().toString() + '_' + Math.random().toString(36).slice(2, 8)
    const response = await axios.get(`${config.API_BASE_URL}/api/captcha`, {
      params: { key: freshKey },
      timeout: 5000
    })
    if (response.data.code === 200) {
      captchaImage.value = response.data.data.image
      captchaKey.value = response.data.data.key
    }
  } catch (error) {
    console.error('获取验证码失败:', error)
    ElMessage.error('获取验证码失败，请重试')
  } finally {
    captchaLoading = false
  }
}

// 手机号验证（已由 checkPhoneAsync 替代）

// 异步校验状态
const usernameCheck = reactive({ checked: false, exists: false })
const emailCheck = reactive({ checked: false, exists: false })
const phoneCheck = reactive({ checked: false, exists: false, isWxUser: false })

// 检查用户名
const checkUsernameAsync = async () => {
  if (!registerForm.username || registerForm.username.length < 3) return
  try {
    const { data } = await axios.get(`${config.API_BASE_URL}/api/check/username`, {
      params: { username: registerForm.username }
    })
    usernameCheck.checked = true
    usernameCheck.exists = data.data.exists
  } catch (e) {
    console.error('检查用户名失败', e)
  }
}

// 检查邮箱
const checkEmailAsync = async () => {
  if (!registerForm.email) return
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(registerForm.email)) return
  try {
    const { data } = await axios.get(`${config.API_BASE_URL}/api/check/email`, {
      params: { email: registerForm.email }
    })
    emailCheck.checked = true
    emailCheck.exists = data.data.exists
  } catch (e) {
    console.error('检查邮箱失败', e)
  }
}

// 检查手机号
const checkPhoneAsync = async () => {
  // 先做格式校验
  if (!registerForm.phone) return
  const phoneRegex = /^1[3-9]\d{9}$/
  if (!phoneRegex.test(registerForm.phone)) {
    ElMessage.warning('请输入正确的手机号码')
    return
  }
  try {
    const { data } = await axios.get(`${config.API_BASE_URL}/api/check/phone`, {
      params: { phone: registerForm.phone }
    })
    phoneCheck.checked = true
    phoneCheck.exists = data.data.exists
    phoneCheck.isWxUser = data.data.isWxUser || false
  } catch (e) {
    console.error('检查手机号失败', e)
  }
}

// 跳转到登录页
const goToLogin = () => {
  router.push({ name: 'login' })
}

// 注册处理
const handleRegister = async () => {
  if (!registerFormRef.value) return
  
  try {
    const valid = await registerFormRef.value.validate()
    if (!valid) return

    if (usernameCheck.exists || emailCheck.exists || phoneCheck.exists) {
      ElMessage.error('请修正表单中的错误后再提交')
      return
    }

    loading.value = true
    
    // 加密密码
    const encryptedPassword = encryptPassword(registerForm.password)
    
    const registerData = {
      username: registerForm.username,
      password: encryptedPassword,
      company: registerForm.company,
      email: registerForm.email,
      phone: registerForm.phone,
      smsCode: registerForm.smsCode,
      captcha: registerForm.captcha,
      captchaKey: captchaKey.value,
      gender: registerForm.gender
    }

    const response = await axios.post(`${config.API_BASE_URL}/api/register`, registerData, {
      timeout: 15000
    })
    const result = response.data

    if (result.code === 200) {
      ElMessage.success(result.message || '注册成功！')

      if (result.data) {
        const token = result.data.token
        localStorage.setItem('token', token)
        localStorage.setItem('userInfo', JSON.stringify(result.data.userInfo))
        // 注册时选择了自定义头像：注册成功后立即上传头像并刷新缓存
        if (avatarFile.value) {
          try {
            const formData = new FormData()
            formData.append('file', avatarFile.value)
            const avatarResp = await axios.post(`${config.API_BASE_URL}/api/user/avatar`, formData, {
              headers: {
                Authorization: token.startsWith('Bearer ') ? token : `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            })
            if (avatarResp.data.code === 200 && avatarResp.data.data) {
              const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
              userInfo.avatar = avatarResp.data.data.avatar
              localStorage.setItem('userInfo', JSON.stringify(userInfo))
            }
          } catch (e) {
            console.error('头像上传失败（可稍后在个人菜单中更换）:', e)
          }
        }
        router.push({ name: 'home' })
      }
    } else {
      ElMessage.error(result.message || '注册失败')
      refreshCaptcha()
    }
  } catch (error) {
    console.error('注册失败:', error)
    
    if (error.code === 'ECONNABORTED') {
      ElMessage.error('请求超时，请检查网络连接')
    } else if (error.response) {
      const errorData = error.response.data
      const errorMsg = errorData?.message || errorData?.data?.message || '请求失败'
      ElMessage.error(`注册失败: ${errorMsg}`)
    } else if (error.request) {
      ElMessage.error('网络错误，请检查网络连接')
    } else {
      ElMessage.error(`请求错误: ${error.message}`)
    }
    
    refreshCaptcha()
  } finally {
    loading.value = false
  }
}

// 返回上一页
const goBack = () => {
  router.go(-1)
}

// 初始化
onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
  refreshCaptcha()
})

// 清理
onBeforeUnmount(() => {
  if (countdownTimer) {
    clearInterval(countdownTimer)
  }
  window.removeEventListener('resize', checkMobile)
})
</script>

<style scoped>
/* 基础样式 */
.register-container {
  min-height: 100vh;
  background: url('./dipian6.jpg') repeat-y;
  background-size: cover;
  background-attachment: fixed;
  padding: 20px;
}

/* 异步校验提示 */
.field-check-tip {
  font-size: 12px;
  margin-top: 5px;
  padding-left: 2px;
}
.field-check-tip.success {
  color: #67c23a;
}
.field-check-tip.error {
  color: #f56c6c;
}
.phone-registered-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 8px 12px;
  background: #fdf6ec;
  border: 1px solid #f5dab1;
  border-radius: 4px;
  font-size: 13px;
  color: #e6a23c;
}
.reset-pwd-btn {
  margin-left: auto;
}

.register-card {
  max-width: 600px;
  margin: 40px auto;
  padding: 40px;
  background-color: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

.register-title {
  text-align: center;
  margin-bottom: 30px;
  color: #0056b3;
  font-size: 28px;
  font-weight: 600;
}

/* 表单样式 */
.register-form {
  margin-bottom: 30px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

/* 手机验证码区域 */
.phone-code-row {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.phone-input {
  flex: 1;
}

.sms-btn {
  width: 120px;
  flex-shrink: 0;
}

.sms-tips {
  font-size: 12px;
  color: #67C23A;
  margin-top: 5px;
}

/* 验证码区域 */
.captcha-row {
  display: flex;
  align-items: center;
  gap: 15px;
}

/* 头像选择 */
.avatar-picker {
  display: flex;
  align-items: center;
  gap: 15px;
}

.avatar-preview {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e4e7ed;
  background: #f5f7fa;
}

.avatar-picker-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-start;
}

.avatar-tip {
  font-size: 12px;
  color: #909399;
}

.captcha-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.captcha-img {
  width: 120px;
  height: 40px;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
}

.refresh-icon {
  margin-left: 8px;
  cursor: pointer;
  color: #606266;
  transition: color 0.3s;
}

.refresh-icon:hover {
  color: #0056b3;
}

/* 按钮区域 */
.register-actions {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 30px;
}

.back-btn,
.register-btn {
  min-width: 120px;
  height: 44px;
  font-size: 16px;
  border-radius: 8px;
}

.register-btn {
  background: linear-gradient(135deg, #0056b3, #1890ff);
  border: none;
}

/* 登录链接 */
.login-link {
  text-align: center;
  color: #666;
  font-size: 14px;
}

.link-btn {
  margin-left: 5px;
  color: #0056b3;
}

.password-hint {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

/* 移动端样式 */
@media (max-width: 768px) {
  .register-container {
    padding: 0;
    background: #f5f7fa;
  }
  
  .register-card {
    margin: 0;
    padding: 20px 16px;
    border-radius: 0;
    box-shadow: none;
    min-height: calc(100vh - 100px);
  }
  
  .mobile-header {
    display: flex;
    align-items: center;
    padding: 16px;
    background: white;
    border-bottom: 1px solid #eee;
    position: sticky;
    top: 0;
    z-index: 100;
  }
  
  .back-icon {
    font-size: 20px;
    cursor: pointer;
    color: #333;
  }
  
  .mobile-title {
    flex: 1;
    text-align: center;
    margin: 0;
    font-size: 18px;
    font-weight: 500;
  }
  
  .register-form :deep(.el-form-item) {
    margin-bottom: 20px;
  }
  
  .register-form :deep(.el-form-item__label) {
    padding: 0 0 8px 0 !important;
    line-height: 1.4;
    font-size: 14px;
  }
  
  .phone-code-row.mobile-column {
    flex-direction: column;
    gap: 10px;
  }
  
  .sms-btn.mobile-sms-btn {
    width: 100%;
    height: 44px;
  }
  
  .captcha-row.mobile-column {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .captcha-img {
    width: 100%;
    height: 44px;
  }
  
  .mobile-actions {
    flex-direction: column;
    gap: 16px;
    margin-top: 32px;
  }
  
  .mobile-register-btn {
    width: 100%;
    height: 48px;
    font-size: 16px;
    border-radius: 8px;
  }
  
  .mobile-login-link {
    padding: 20px 0;
    border-top: 1px solid #eee;
    margin-top: 20px;
  }
  
  .mobile-bottom-bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: white;
    padding: 12px 16px;
    border-top: 1px solid #eee;
    z-index: 100;
  }
  
  .mobile-login-btn {
    width: 100%;
    text-align: center;
    color: #0056b3;
    font-weight: 500;
  }
  
  .mobile-hint {
    font-size: 11px;
    line-height: 1.4;
  }
  
  /* 优化输入框在iOS上的表现 */
  :deep(.el-input__wrapper) {
    border-radius: 8px;
  }
  
  :deep(input) {
    font-size: 16px !important; /* 防止iOS缩放 */
    -webkit-appearance: none;
  }
}

/* 平板样式 */
@media (min-width: 769px) and (max-width: 1024px) {
  .register-card {
    max-width: 80%;
    padding: 30px;
  }
}

/* 防止键盘遮挡输入框 */
@media (max-width: 768px) {
  .register-container {
    padding-bottom: 80px;
  }
}

/* 暗色模式支持 */
@media (prefers-color-scheme: dark) {
  .register-card {
    background-color: rgba(30, 30, 30, 0.95);
    color: #fff;
  }
  
  .mobile-header {
    background: #1a1a1a;
    border-bottom-color: #333;
  }
  
  .back-icon {
    color: #fff;
  }
  
  :deep(.el-input__wrapper) {
    background-color: #2a2a2a;
    border-color: #444;
  }
  
  :deep(.el-input__inner) {
    color: #fff;
  }
  
  .password-hint {
    color: #aaa;
  }
}
</style>